import {useEffect, useRef} from 'react';
import {useKeyboardStore} from '../store/keyboard';

const KEYBOARD_THRESHOLD = 150; // 키보드로 판단할 최소 높이 변화
const FOCUS_WINDOW_MS = 500; // input 상호작용 후 이 시간 내에 viewport 변화시만 키보드로 판단

export function useKeyboardDetect() {
    const {setKeyboardOpen} = useKeyboardStore();
    const initialHeightRef = useRef<number>(0);
    const isKeyboardOpenRef = useRef<boolean>(false);
    const lastInputInteractionTimeRef = useRef<number>(0);

    useEffect(() => {
        // 초기 viewport 높이 저장
        const updateInitialHeight = () => {
            const height = window.visualViewport?.height || window.innerHeight;
            // 키보드가 닫힌 상태에서만 초기 높이 업데이트
            if (!isKeyboardOpenRef.current) {
                initialHeightRef.current = height;
            }
        };

        // 약간의 딜레이 후 초기 높이 설정 (페이지 로드 안정화)
        setTimeout(updateInitialHeight, 300);

        // input과의 상호작용 시점 기록 (focus 또는 click)
        const updateInteractionTime = (target: HTMLElement) => {
            const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
            if (isInput) {
                lastInputInteractionTimeRef.current = Date.now();
            }
        };

        const handleFocusIn = (e: FocusEvent) => {
            updateInteractionTime(e.target as HTMLElement);
        };

        // 이미 focus된 input을 다시 탭하는 경우를 위한 click 핸들러
        const handleClick = (e: MouseEvent) => {
            updateInteractionTime(e.target as HTMLElement);
        };

        const handleViewportResize = () => {
            const currentHeight = window.visualViewport?.height || window.innerHeight;
            const heightDiff = initialHeightRef.current - currentHeight;
            const timeSinceInteraction = Date.now() - lastInputInteractionTimeRef.current;

            // 키보드 열림 조건: input 상호작용 직후 + viewport 감소
            if (heightDiff > KEYBOARD_THRESHOLD) {
                // 상호작용 직후(500ms 이내)에만 키보드로 판단
                if (timeSinceInteraction < FOCUS_WINDOW_MS && !isKeyboardOpenRef.current) {
                    isKeyboardOpenRef.current = true;
                    setKeyboardOpen(true);
                } else if (!isKeyboardOpenRef.current) {
                    // 상호작용 없이 viewport 감소 = 창 크기 조절
                    initialHeightRef.current = currentHeight;
                }
            } else if (heightDiff < 50 && isKeyboardOpenRef.current) {
                // 키보드 닫힘 (이전에 열려있었던 경우에만)
                isKeyboardOpenRef.current = false;
                setKeyboardOpen(false);
                // 키보드 닫힌 후 초기 높이 업데이트
                initialHeightRef.current = currentHeight;
            }
        };

        // focusout은 보조 역할 (다른 곳 터치 시)
        const handleFocusOut = (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

            if (isInput && isKeyboardOpenRef.current) {
                setTimeout(() => {
                    const activeElement = document.activeElement;
                    const isStillInput =
                        activeElement?.tagName === 'INPUT' ||
                        activeElement?.tagName === 'TEXTAREA' ||
                        (activeElement as HTMLElement)?.isContentEditable;

                    if (!isStillInput) {
                        isKeyboardOpenRef.current = false;
                        setKeyboardOpen(false);
                    }
                }, 100);
            }
        };

        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('click', handleClick);
        window.visualViewport?.addEventListener('resize', handleViewportResize);
        document.addEventListener('focusout', handleFocusOut);

        return () => {
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('click', handleClick);
            window.visualViewport?.removeEventListener('resize', handleViewportResize);
            document.removeEventListener('focusout', handleFocusOut);
        };
    }, [setKeyboardOpen]);
}
